import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  House,
  Ticket,
  FolderOpen,
  Users,
  Buildings,
  ChartBar,
  SignOut,
  Bell,
  Plus,
  GearSix,
  List,
  CaretUpDown,
  Check,
  ClockCounterClockwise,
  Sparkle,
  WarningCircle,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { canCreateTickets, cn, initials, ROLE_LABELS, timeAgo } from "../lib/utils";
import UpdateTag from "./UpdateTag";
import GlobalSearch from "./GlobalSearch";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["super_admin", "project_manager", "team_member", "client_admin", "client_user"] },
  { to: "/tickets", label: "Tickets", icon: Ticket, roles: ["super_admin", "project_manager", "team_member", "client_admin", "client_user"] },
  { to: "/tickets?tab=urgent", label: "Urgent Tickets", icon: WarningCircle, roles: ["super_admin", "project_manager", "team_member"] },
  { to: "/projects", label: "Projects", icon: FolderOpen, roles: ["super_admin", "project_manager", "client_admin", "client_user"] },
  { to: "/organizations", label: "Clients", icon: Buildings, roles: ["super_admin", "project_manager"] },
  { to: "/users", label: "Team & Users", icon: Users, roles: ["super_admin", "project_manager", "client_admin"] },
  { to: "/reports", label: "Reports", icon: ChartBar, roles: ["super_admin", "project_manager"] },
  { to: "/audit", label: "Audit Log", icon: ClockCounterClockwise, roles: ["super_admin"] },
];

function BrandMark({ company }) {
  const variant = company?.brand_variant || "logo";
  const name = company?.name || "Support By ERP4U";
  if (variant === "name") {
    return <span className="font-display font-semibold text-white truncate">{name}</span>;
  }
  if (variant === "logomark_name") {
    return (
      <span className="flex items-center gap-2 min-w-0">
        {company?.icon_url
          ? <img src={company.icon_url} alt="" className="w-8 h-8 rounded-md object-contain bg-white/5 shrink-0" />
          : <span className="w-8 h-8 rounded-md bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{initials(name)}</span>}
        <span className="font-display font-semibold text-white truncate">{name}</span>
      </span>
    );
  }
  // 'logo'
  return company?.logo_url
    ? <img src={company.logo_url} alt={name} className="max-h-10 w-auto max-w-[190px] object-contain" />
    : <span className="font-display font-semibold text-white truncate">{name}</span>;
}

function BrandStatic({ user, onNavigate }) {
  return (
    <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" data-testid="brand-link" onClick={onNavigate}>
      <BrandMark company={user?.company} />
    </Link>
  );
}

function BrandSwitcher({ user, switchTenant }) {
  const multiMembership = user?.memberships?.length > 1;

  if (!multiMembership) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="org-switcher-trigger"
          className="flex items-center gap-2 w-full min-w-0 rounded-md px-1 py-1 hover:bg-slate-800/60 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
          aria-label="Switch organization"
        >
          <BrandMark company={user?.company} />
          <CaretUpDown size={14} className="text-slate-400 shrink-0 ml-auto" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className="w-56 bg-slate-800 border-slate-700 text-slate-200"
        data-testid="org-switcher-menu"
      >
        {user.memberships.map((m) => {
          const isActive = m.tenant_id === user.tenant_id;
          return (
            <DropdownMenuItem
              key={m.tenant_id}
              onSelect={() => {
                if (!isActive) {
                  switchTenant(m.tenant_id).then(() => window.location.reload());
                }
              }}
              data-testid={`org-option-${m.tenant_id}`}
              className={cn(
                "flex items-center gap-2 cursor-pointer focus:bg-slate-700 focus:text-white",
                isActive ? "text-white" : "text-slate-300"
              )}
            >
              <Check
                size={14}
                className={cn("shrink-0", isActive ? "opacity-100 text-blue-400" : "opacity-0")}
              />
              <span className="truncate">{m.company_name}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarBody({ user, switchTenant, items, logout, navigate, onNavigate = () => {} }) {
  const multiMembership = user?.memberships?.length > 1;

  return (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-slate-800 shrink-0">
        {multiMembership ? (
          <BrandSwitcher user={user} switchTenant={switchTenant} />
        ) : (
          <BrandStatic user={user} onNavigate={onNavigate} />
        )}
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="px-5 mb-2 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Workspace</div>
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`nav-${to.replace("/", "")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center px-4 py-2 text-sm font-medium rounded-md mx-2 my-0.5 transition-colors",
                isActive
                  ? "bg-slate-800 text-white"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
              )
            }
          >
            <Icon size={18} weight="regular" className="mr-3" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-800 p-4 shrink-0">
        <NavLink
          to="/settings"
          data-testid="nav-settings"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center px-2 py-2 mb-2 text-sm font-medium rounded-md transition-colors",
              isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
            )
          }
        >
          <GearSix size={18} weight="regular" className="mr-3" /> Settings
        </NavLink>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-blue-600/20 text-blue-300 flex items-center justify-center text-xs font-semibold">
            {initials(user.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white truncate" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-xs text-slate-500 truncate">{ROLE_LABELS[user.role]}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            onNavigate();
            await logout();
            navigate("/login");
          }}
          data-testid="logout-button"
          className="w-full inline-flex items-center justify-center gap-2 text-xs text-slate-300 border border-slate-700 hover:bg-slate-800 rounded-md px-3 py-2 transition-colors"
        >
          <SignOut size={14} /> Sign out
        </button>
        <a href="https://webteampl.com" target="_blank" rel="noreferrer"
          className="block text-center text-[10px] text-slate-500 hover:text-slate-300 mt-3">
          A WebTeam product · webteampl.com
        </a>
      </div>
    </div>
  );
}

const ACTION_LABELS = {
  sla_breached: "SLA breached",
  sla_approaching: "SLA approaching",
};

const humanizeAction = (a) => ACTION_LABELS[a] || (a || "").replace(/_/g, " ");

const linkify = (text) =>
  String(text || "").split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{part}</a>
      : part
  );

function WhatsNew() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ updates: [], unseen: 0 });
  const load = () => api.get("/updates").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openPanel = () => {
    const next = !open;
    setOpen(next);
    if (next && data.unseen > 0) {
      api.post("/updates/seen").then(() => setData((d) => ({ ...d, unseen: 0 }))).catch(() => {});
    }
  };
  return (
    <div className="relative">
      <button
        type="button"
        className="p-2 rounded-md hover:bg-slate-100 text-slate-500 relative"
        data-testid="whats-new-button"
        onClick={openPanel}
        aria-label="What's New"
      >
        <Sparkle size={18} />
        {data.unseen > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-600" data-testid="whats-new-dot" />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 max-h-[28rem] overflow-y-auto surface-card z-30" data-testid="whats-new-panel">
            <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">What's New</div>
            {data.updates.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-400">No updates yet.</div>}
            {data.updates.map((u) => (
              <div key={u.id} className="px-4 py-3 border-b border-slate-50">
                <div className="flex items-center justify-between gap-2">
                  <UpdateTag tag={u.tag} />
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{timeAgo(u.published_at)}</span>
                </div>
                <div className="text-sm font-medium text-slate-800 mt-1.5">{u.title}</div>
                {u.body && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap leading-relaxed">{linkify(u.body)}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="relative">
      <button
        type="button"
        className="p-2 rounded-md hover:bg-slate-100 text-slate-500 relative"
        data-testid="notifications-button"
        onClick={() => { const next = !open; setOpen(next); if (next) load(); }}
        aria-label="Notifications"
      >
        <Bell size={18} />
        {items.length > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-600" />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto surface-card z-30" data-testid="notifications-panel">
            <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Notifications</div>
            {items.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-400">No recent activity.</div>}
            {items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => { setOpen(false); navigate(`/tickets/${n.ticket_id}`); }}
                className="w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50"
              >
                <div className="text-sm text-slate-800"><span className="font-medium">{n.actor_name}</span> {humanizeAction(n.action)}</div>
                {(n.ticket_no || n.subject) && <div className="text-xs text-slate-500 truncate">{n.ticket_no}{n.subject ? ` · ${n.subject}` : ""}</div>}
                <div className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, switchTenant } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  if (!user) return null;
  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop rail */}
      <aside className="hidden lg:flex w-64 bg-slate-900 text-slate-300 flex-col border-r border-slate-800 sticky top-0 h-screen" data-testid="sidebar">
        <SidebarBody user={user} switchTenant={switchTenant} items={items} logout={logout} navigate={navigate} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-slate-900 border-slate-800 text-slate-300 [&>button]:text-slate-400 [&>button]:hover:text-white [&>button]:opacity-100" data-testid="sidebar-drawer">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarBody user={user} switchTenant={switchTenant} items={items} logout={logout} navigate={navigate} onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6 sticky top-0 z-10" data-testid="topbar">
          <button type="button" className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-600"
            onClick={() => setDrawerOpen(true)} data-testid="sidebar-toggle" aria-label="Open menu">
            <List size={20} />
          </button>
          <GlobalSearch />
          <div className="flex items-center gap-2 ml-auto">
            {canCreateTickets(user) && (
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => navigate("/tickets/new")}
                data-testid="topbar-new-ticket-button"
              >
                <Plus size={14} weight="bold" className="mr-1.5" /> <span className="hidden sm:inline">New ticket</span><span className="sm:hidden">New</span>
              </Button>
            )}
            <WhatsNew />
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 min-w-0">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
